/* eslint-disable css-modules/no-undef-class, css-modules/no-unused-class, no-nested-ternary, no-useless-return */
import { CardLoader, Tooltip, Icon } from 'learning-panel-components';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { translate } from 'translations';
import { badges as badgesMapping } from 'utils';
import styles from './achievements.module.scss';
import AchievementsCollapsed from './achievementsCollapsed';

const Achievements: FC<AchievementsProps> = (props: AchievementsProps) => {
    const { userDevCoins, activityBadges, showLeaderboard, fullWidth, isLoading } = props;
    const [isExpanded, setIsExpanded] = useState(false);
    const [windowWidthSize, setWindowWidthSize] = useState<number>(0);
    const [remainingBadges, setRemainingBadges] = useState<string[]>([]);

    const calcSize = useCallback(() => {
        const width =
            window.innerWidth -
            (fullWidth
                ? 0.875 * parseFloat(getComputedStyle(document.documentElement).fontSize)
                : 21.5 * parseFloat(getComputedStyle(document.documentElement).fontSize));
        return width * 0.65 * (showLeaderboard ? 0.3 : 0.5);
    }, [fullWidth, showLeaderboard]);

    useEffect(() => {
        setWindowWidthSize(calcSize());
    }, [calcSize, fullWidth, showLeaderboard]);

    useEffect(() => {
        const tempRemainingBadges = [];
        for (const key in badgesMapping) {
            if (!activityBadges.includes(key)) {
                tempRemainingBadges.push(key);
            }
        }

        setRemainingBadges(tempRemainingBadges);
    }, [activityBadges]);

    const getBadges = (badges: string[], earnedBadges: boolean): ReactNode[] => {
        const badgesElements: ReactNode[] = [];
        const numberOfBadgesToShow = Math.ceil(windowWidthSize / 165);

        badges.forEach((badge, index) => {
            if (numberOfBadgesToShow >= Number(index) + 1) {
                badgesElements.push(
                    <div key={index}>
                        <div>
                            <Tooltip title={translate(badgesMapping[badge].descriptionKey)} placement="top" interactive>
                                <span>
                                    <Icon
                                        name={
                                            earnedBadges
                                                ? badgesMapping[badge].earnIconName
                                                : badgesMapping[badge].notEarnIconName
                                        }
                                        isPng
                                        className={styles['icon-wrapper']}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                );
            }
        });

        return badgesElements;
    };

    return isLoading ? (
        <div className={styles.achievements} style={{ '--windowWidthSize': showLeaderboard ? '30%' : '50%' } as any}>
            <CardLoader width="100%" height="100%" />
        </div>
    ) : isExpanded ? (
        <div className={styles.achievements} style={{ '--windowWidthSize': showLeaderboard ? '30%' : '50%' } as any}>
            <div className={styles.top}>
                <div className={styles.title}>{translate('Achievements')}</div>
                <div className={styles['icons-wrapper']}>
                    <div
                        onClick={() => {
                            setIsExpanded(false);
                        }}
                    >
                        <Icon data-testid="arrow-icon" name={'close'} className={styles.icon} />
                    </div>
                </div>
            </div>
            <div className={styles['achievements-data-wrapper']}>
                <hr />
                <div className={styles['personal-wrapper']}>
                    <div className={styles['title-text']} title={translate('PersonalDevCoins')}>
                        {translate('PersonalDevCoins')}
                    </div>
                    <div className={styles['coins-wrapper']}>
                        <Icon name="devCoin" />
                        <div className={styles['coins-text']}>{userDevCoins.toLocaleString()}</div>
                    </div>
                </div>
                <hr />

                <div className={styles['team-wrapper']}>
                    <div
                        className={styles['title-text']}
                        title={`${translate('ActivityBadges')} (${activityBadges.length})`}
                    >{`${translate('ActivityBadges')} (${activityBadges.length})`}</div>
                    <div className={styles['coins-wrapper']}>
                        {activityBadges.length > 0 ? getBadges(activityBadges, true) : <div></div>}
                    </div>
                </div>
                <hr />

                <div className={styles['more-to-earn-wrapper']}>
                    <div
                        className={styles['title-text']}
                        title={`${translate('MoreToEarn')} (${remainingBadges.length})`}
                    >{`${translate('MoreToEarn')} (${remainingBadges.length})`}</div>
                    <div className={styles['coins-wrapper']}>{getBadges(remainingBadges, false)}</div>
                </div>
            </div>
        </div>
    ) : (
        <AchievementsCollapsed
            isLoading={false}
            userCoins={userDevCoins}
            userBadges={activityBadges}
            onExpand={setIsExpanded}
        />
    );
};

interface AchievementsProps {
    showLeaderboard: boolean;
    fullWidth: boolean;
    userDevCoins: number;
    activityBadges: string[];
    onSeeAll?: Function;
    isLoading?: boolean;
}

export default Achievements;
